<template>
    <section>
        <template v-if="order">
            <article class="row">
                <div class="col-md-6">
                    <order-address title="Shipping Address"
                                   :disabled="true"
                                   :region="region"
                                   v-bind.sync="order.shipping_address"/>
                </div>
                <div class="col-md-6">
                    <order-address title="Billing Address"
                                   :disabled="true"
                                   :region="region"
                                   v-bind.sync="order.billing_address"/>
                </div>
            </article>
            <article class="row">
                <div class="col-md-4">
                    <order-return-remarks :disabled="isLoading"/>
                </div>
                <div class="col-md-4">
                    <order-return-reason-code :disabled="isLoading"/>
                </div>
                <div class="col-md-4">
                    <order-discount-add :disabled="isLoading"
                                        :typeSelected="typeSelected"
                                        :discountCode="discount.value"
                                        :fixedValue="discount.value"
                                        :percentValue="discount.value"
                                        v-on:update:typeSelected="setDiscountModel($event, 'type')"
                                        v-on:update:discountCode="setDiscountModel($event, 'code')"
                                        v-on:update:fixedValue="setDiscountModel($event, 'fixed')"
                                        v-on:update:percentValue="setDiscountModel($event, 'percent')"
                                        v-on:applyFixed="addDiscount"
                                        v-on:applyPercent="addDiscount"
                                        v-on:applyDiscountCode="addDiscount"/>
                </div>
            </article>
            <article class="row m-b-3">
                <div class="col-md-12 p-a-2">
                    <el-progress class="p-b-3"
                                 status="success"
                                 :class="[progress < 100 ? 'fade-in' : 'fade-out']"
                                 :percentage="progress"
                                 :show-text="false"/>
                    <order-return-products/>
                </div>
            </article>
            <article v-if="!region.includes('amazon')" class="row m-t-1">
              <div class="col-md-12">
                <order-actions :order="order" :products="mappedProducts" :region="region" :d365Order="d365Order"></order-actions>
              </div>
            </article>
            <article class="row">
                <div class="col-md-9">
                    <order-return-payment/>
                </div>
                <div class="col-md-3">
                    <order-note :note.sync="noteModel" :disabled="isLoading"/>
                </div>
            </article>
            <hr>
            <article class="row">
                <div class="col-md-12">
                    <order-return-controls/>
                </div>
            </article>
        </template>
        <app-loader v-else-if="isLoading"/>
        <el-dialog :visible.sync="isErpStatusVisible" width="30%">
            <template slot="title">
                <h4>
                    <p>{{d365Message}}</p>
                    <p>{{d365Issue}}</p>
                    <p>Please see below:</p>
                </h4>
            </template>
            <log-on-detail-table :d365Order="d365Order"/>
            <span slot="footer" class="dialog-footer">
                <el-button @click="isErpStatusVisible = false">Close</el-button>
            </span>
        </el-dialog>
    </section>
</template>

<script>
import {
  required,
  numeric,
  minValue,
  decimal,
  maxLength, requiredIf,
} from 'vuelidate/lib/validators';
  import {mapActions, mapState, mapMutations, mapGetters} from 'vuex';
  import {
    ORDER_RETURN_SET_NOTE,
    ORDER_RETURN_SET_DISCOUNT,
    ORDER_RETURN_DISCOUNT_PRICE_RULE_ADD,
    ORDER_RETURN_GET_ROUNDING_ERROR,
  } from '../../../store/modules/order/return/types';
  import {
    LOGON_CANNOT_MODIFY_ORDER,
  } from '../../../lang/en/notify';
  import {
    ORDER_RETURN_MINIMUM_SUBTOTAL_REFUND_AMOUNT,
    ORDER_RETURN_MINIMUM_TOTAL_REFUND_AMOUNT,
    ORDER_RETURN_MINIMUM_PRODUCT_QUANTITY,
    LOGON_RA_REMARKS_MAX_LENGTH,
    ORDER_RETURN_MINIMUM_SHIPPING_PRICE,
  } from '../../../lang/en/constants';
  import AppLoader from '../../ui/AppLoader';
  import OrderNote from '../Shared/OrderNote';
  import OrderAddress from '../Shared/OrderAddress';
  import OrderActions from '../Shared/OrderActions';
  import OrderDiscountAdd from '../Shared/OrderDiscountAdd';
  import OrderReturnPayment from './OrderReturnPayment';
  import OrderReturnRemarks from './OrderReturnRemarks';
  import OrderReturnControls from './OrderReturnControls';
  import OrderReturnProducts from './OrderReturnProducts';
  import OrderReturnReasonCode from './OrderReturnReasonCode';
  import D365DetailTable from '../Shared/D365DetailTable';

  export default {
    name: 'OrderReturnUpdatePage',
    components: {
      AppLoader,
      OrderNote,
      OrderAddress,
      OrderActions,
      OrderDiscountAdd,
      OrderReturnRemarks,
      OrderReturnPayment,
      OrderReturnProducts,
      OrderReturnControls,
      OrderReturnReasonCode,
      LogOnDetailTable: D365DetailTable,
    },
    computed: {
      ...mapState({
        isLoading: state => state.orderReturn.loading,
        region: state => state.orderReturn.region,
        order: state => state.orderReturn.order,
        raRemarks: state => state.orderReturn.raRemarks,
        textRemarks: state => state.orderReturn.textRemarks,
        reasonCode: state => state.orderReturn.reasonCode,
        discount: state => state.orderReturn.discount,
        typeSelected: state => state.orderReturn.discount.selected,
        products: state => state.orderReturn.products,
        shipping: state => state.orderReturn.shipping,
        note: state => state.orderReturn.note,
        d365Order: state => state.orderReturn.d365Order,
        d365Issue: state => state.orderReturn.d365Error,
        originalSubtotalPrice: state => (state.orderReturn.order
          ? state.orderReturn.order.subtotal_price
          : 0),
        refundSubtotalPrice: state => state.orderReturn.draftRA.subtotal_price,
        originalTotalPrice: state => (state.orderReturn.order
          ? state.orderReturn.order.total_price
          : 0),
        refundTotalPrice: state => state.orderReturn.draftRA.total_price,
        mappedProducts: (state) => {
          return {
            items: state.orderReturn.products.map((p) => {
              return {
                original: p,
                ...p.product,
                variant: p.variant,
                line_item: p.lineItem,
              }
            })
          }
        }
      }),
      ...mapGetters({
        getRoundingErrorCents: ORDER_RETURN_GET_ROUNDING_ERROR,
      }),
      progress() {
        return (this.products.length / this.order.line_items.length) * 100;
      },
      noteModel: {
        get() {
          return this.note;
        },
        set(value) {
          this.setNote(value);
        },
      },
    },
    methods: {
      ...mapMutations({
        setNote: ORDER_RETURN_SET_NOTE,
        setDiscount: ORDER_RETURN_SET_DISCOUNT,
      }),
      ...mapActions({
        addDiscount: ORDER_RETURN_DISCOUNT_PRICE_RULE_ADD,
      }),
      setDiscountModel(value, type) {
        let update = {};

        switch (type) {
          case 'type':
            update = {
              selected: value,
              value: '',
            };
            break;
          case 'code':
          case 'fixed':
          case 'percent':
            update = {value};
            break;
        }

        const discount = {...this.discount, ...update};

        this.setDiscount(discount);
      },
    },
    watch: {
      d365Issue: function(d365Issue) {
        if (d365Issue) {
          this.isErpStatusVisible = true;
        }
      },
    },
    data() {
      return {
        d365Message: LOGON_CANNOT_MODIFY_ORDER,
        isErpStatusVisible: false,
      };
    },
    provide() {
      return {
        $v: this.$v,
      };
    },
    validations() {
      return {
        raRemarks: {
          required,
          maxLength: maxLength(LOGON_RA_REMARKS_MAX_LENGTH),
        },
        textRemarks: {
          required: requiredIf(function() {
            return this.raRemarks === 'CF';
          })
        },
        reasonCode: {
          required,
        },
        products: {
          $each: {
            returnItem: {
              quantity: {
                required,
                numeric,
                minValue: minValue(ORDER_RETURN_MINIMUM_PRODUCT_QUANTITY),
                maxValue(value, {inventory}) {
                  return value <= inventory;
                },
              },
            },
          },
        },
        shipping: {
          price: {
            decimal,
            minValue: minValue(ORDER_RETURN_MINIMUM_SHIPPING_PRICE),
            maxValue(value, {max_price}) {
              return value <= max_price;
            },
          },
        },
        note: {
          required,
        },
        refundSubtotalPrice: {
          minValue: minValue(ORDER_RETURN_MINIMUM_SUBTOTAL_REFUND_AMOUNT),
          maxValue: (refundSubtotalPrice, {originalSubtotalPrice}) => {
            const cents = this.getRoundingErrorCents;
            const maxRefundSubtotal = Number(originalSubtotalPrice) + cents;
            return Number(refundSubtotalPrice) <= maxRefundSubtotal;
          },
        },
        refundTotalPrice: {
          minValue: minValue(ORDER_RETURN_MINIMUM_TOTAL_REFUND_AMOUNT),
          maxValue: (refundTotalPrice, {originalTotalPrice}) => {
            const cents = this.getRoundingErrorCents;
            const maxRefundTotal = Number(originalTotalPrice) + cents;
            return Number(refundTotalPrice) <= maxRefundTotal;
          },
        },
      };
    },
  };
</script>
